import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../../store/store';
import { api } from '../../api/remembrApi';
import type {
  BorrowRequestsQuery,
  BorrowRequestListResponse,
  BorrowRequestDetails,
  BorrowRequestActionSchema,
  BorrowRequestActionResponse,
  BorrowRequestResponse,
  SentBorrowRequestListResponse,
  SentBorrowRequest,
} from '../../api/borrowTypes';
import type { ClaimResponse } from '../../api/sharingTypes';
// FE-255 (#49/#59/#69) — after an in-crew approve we refetch the
// affected group's inventory so the owner's row CTA flips to "Lent
// out" without a manual re-nav. Importing the thunk action-creator
// (not the slice's reducer) keeps the dependency one-directional:
// groupsSlice never imports borrowRequestsSlice, so there's no cycle.
import { loadGroupInventory } from '../sharing/groupsSlice';

// ===============================
// OPTIMISTIC-MUTATION TYPES (FE-255 PR1)
// ===============================

/**
 * Snapshot of state taken at the moment an optimistic mutation
 * fires, used to restore on .rejected. We keep one record per
 * in-flight thunk dispatch (keyed by action.meta.requestId), so
 * two mutations on different rows can roll back independently.
 *
 * `prevPendingCount` is included on every operation because
 * approve/deny decrement it optimistically and cancel doesn't touch
 * it — bundling it on the same shape keeps the rollback code branch-
 * free at the cost of one extra number per snapshot.
 */
type InflightOperation =
  | {
      type: 'approve' | 'deny';
      rowId: number;
      prevStatus: string;
      prevPendingCount: number;
    }
  | {
      type: 'cancel';
      rowId: number;
      prevIndex: number;
      prevRow: BorrowRequestResponse;
    };

// ===============================
// STATE INTERFACE
// ===============================

interface BorrowRequestsState {
  // Received Requests (as item owner)
  receivedRequests: BorrowRequestDetails[];
  
  // Sent Requests (as requester) - track user's own requests
  sentRequests: BorrowRequestResponse[];

  // FE-255 PR1 — optimistic-mutation bookkeeping. Keyed by the
  // thunk's auto-generated `requestId` (action.meta.requestId), so two
  // mutations on different rows in flight at the same time roll back
  // independently. Cleared on .fulfilled and .rejected.
  inflight: Record<string, InflightOperation>;

  // Selected request for detailed view
  selectedRequest: BorrowRequestDetails | null;
  
  // Filter & View State
  currentFilter: 'all' | 'pending' | 'approved' | 'denied' | 'expired';
  activeTab: 'received' | 'sent';
  
  // Loading States
  loading: {
    received: boolean;
    sent: boolean;
    managing: boolean;
    refreshing: boolean;
    claiming: boolean;
  };
  
  // Error Handling
  error: string | null;
  managementError: string | null;
  
  // Statistics
  pendingCount: number;
  totalReceived: number;
  totalSent: number;
  
  // UI State
  lastUpdated: string | null;
  autoRefreshEnabled: boolean;
}

// ===============================
// INITIAL STATE
// ===============================

const initialState: BorrowRequestsState = {
  receivedRequests: [],
  sentRequests: [],
  inflight: {},
  selectedRequest: null,
  currentFilter: 'all',
  activeTab: 'received',
  loading: {
    received: false,
    sent: false,
    managing: false,
    refreshing: false,
    claiming: false,
  },
  error: null,
  managementError: null,
  pendingCount: 0,
  totalReceived: 0,
  totalSent: 0,
  lastUpdated: null,
  autoRefreshEnabled: true,
};

// ===============================
// ASYNC THUNKS
// ===============================

// Load received borrow requests (as owner)
export const loadReceivedRequests = createAsyncThunk<
  BorrowRequestListResponse,
  BorrowRequestsQuery | undefined,
  { state: RootState }
>(
  'borrowRequests/loadReceivedRequests',
  async (params = {}, { getState, rejectWithValue }) => {
    try {
      console.log('📥 BorrowRequests: Loading received requests with params:', params);
      
      const response = await api.getBorrowRequests(params);
      
      console.log('✅ BorrowRequests: Received requests loaded:', {
        requestCount: response.requests.length,
        pendingCount: response.total_pending,
        totalAll: response.total_all
      });
      
      return response;
    } catch (error: any) {
      console.error('❌ BorrowRequests: Failed to load received requests:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to load requests');
    }
  }
);

// Claim an approved sent request
export const claimSentRequest = createAsyncThunk<
  { requestId: number; response: ClaimResponse },
  { requestId: number; message?: string }
>(
  'borrowRequests/claimSentRequest',
  async ({ requestId, message }, { rejectWithValue }) => {
    try {
      const response = await api.claimSentBorrowRequest(requestId, message ? { message } : {});
      return { requestId, response };
    } catch (error: any) {
      console.error('❌ BorrowRequests: Failed to claim request:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to claim request');
    }
  }
);

// Load sent borrow requests (as requester)
export const loadSentRequests = createAsyncThunk<
  SentBorrowRequestListResponse,
  void,
  { state: RootState }
>(
  'borrowRequests/loadSentRequests',
  async (_, { rejectWithValue }) => {
    try {
      console.log('📤 BorrowRequests: Loading sent requests');
      const response = await api.getSentBorrowRequests();
      return response;
    } catch (error: any) {
      console.error('❌ BorrowRequests: Failed to load sent requests:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to load sent requests');
    }
  }
);

// Cancel a sent request
export const cancelSentRequest = createAsyncThunk<
  { requestId: number },
  { requestId: number },
  { state: RootState }
>(
  'borrowRequests/cancelSentRequest',
  async ({ requestId }, { rejectWithValue }) => {
    try {
      await api.cancelSentBorrowRequest(requestId);
      return { requestId };
    } catch (error: any) {
      console.error('❌ BorrowRequests: Failed to cancel sent request:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to cancel request');
    }
  }
);

// Approve a borrow request
//
// FE-255 (#49/#59/#69) — On a successful approve, the item passes into
// the requester's custody. The owner's GroupDetail inventory row needs
// its CTA to flip "Borrow"/"With you" → "Lent out" WITHOUT a manual
// re-navigation. Two-part fix, both keyed on the affected group:
//
//   1. Optimistic — groupsSlice listens to approveBorrowRequest.fulfilled
//      and flips the matching cached row's availability_state →
//      'in_use_by_member'/'lent_out' + sets current_custodian_user_id
//      immediately (see the extraReducer in groupsSlice.ts). This is
//      what makes the CTA flip with zero network latency.
//   2. Authoritative — we dispatch loadGroupInventory(replace) here so
//      the BE's real custody_since / availability_state reconcile the
//      optimistic guess on the next tick.
//
// We resolve the affected group_id / item_id / requester_user_id from
// the cached received row (`getState`) since the action arg only
// carries `requestId`. Marketplace requests (group_id == null) skip
// BOTH paths — there's no group-inventory cache to invalidate.
//
// The fulfilled payload is widened to carry these ids so the
// groupsSlice extraReducer (which can't see the action arg's resolved
// row) has everything it needs without re-reading borrowRequests state.
export interface ApproveBorrowFulfilled {
  response: BorrowRequestActionResponse;
  /** Affected group (null for marketplace requests — no cache to flip). */
  groupId: number | null;
  itemId: number | null;
  /** The requester — becomes the new custodian on approve. */
  borrowerUserId: number | null;
}

export const approveBorrowRequest = createAsyncThunk<
  ApproveBorrowFulfilled,
  { requestId: number; responseMessage?: string },
  { state: RootState }
>(
  'borrowRequests/approveBorrowRequest',
  async ({ requestId, responseMessage }, { getState, dispatch, rejectWithValue }) => {
    try {
      console.log('✅ BorrowRequests: Approving request:', requestId);

      // Resolve the affected group/item BEFORE the network call so we
      // capture the row while it's still in cache (the optimistic
      // .pending flip doesn't drop it, but a concurrent refetch could).
      const row = getState().borrowRequests.receivedRequests.find(
        (r) => r.id === requestId,
      );
      const groupId = (row?.group_id ?? null) as number | null;
      const itemId = (row?.item_id ?? null) as number | null;
      const borrowerUserId = (row?.requester_user_id ?? null) as number | null;

      const action: BorrowRequestActionSchema = {
        action: 'APPROVE',
        response_message: responseMessage,
      };

      const response = await api.manageBorrowRequest(requestId, action);

      console.log('✅ BorrowRequests: Request approved successfully:', {
        requestId,
        groupId,
        itemId,
        shareCreated: response.share_created,
        shareUrl: response.share_url ? 'Created' : 'None'
      });

      // FE-255 — authoritative refetch of the affected group's inventory
      // so the row's custody (availability_state / current_custodian_user_id
      // / custody_since) reflects the server's truth. Skipped for
      // marketplace requests (group_id == null): they don't live in any
      // group-inventory cache, so refetching would be a no-op at best and
      // a wasted round-trip at worst. Fire-and-forget — the optimistic
      // flip in groupsSlice already updated the CTA; this just reconciles.
      if (groupId != null) {
        dispatch(
          loadGroupInventory({
            groupId,
            limit: 50,
            offset: 0,
            mode: 'replace',
          }) as any,
        );
      }

      return { response, groupId, itemId, borrowerUserId };
    } catch (error: any) {
      console.error('❌ BorrowRequests: Failed to approve request:', error);
      // FastAPI surfaces the human reason in `detail` ("Borrow request
      // not found or cannot be modified" on stale-state 404s). Prefer
      // it; fall back to `.message` for any non-FastAPI endpoints.
      return rejectWithValue(
        error.response?.data?.detail ||
          error.response?.data?.message ||
          'Failed to approve request',
      );
    }
  }
);

// Deny a borrow request
export const denyBorrowRequest = createAsyncThunk<
  BorrowRequestActionResponse,
  { requestId: number; responseMessage?: string },
  { state: RootState }
>(
  'borrowRequests/denyBorrowRequest',
  async ({ requestId, responseMessage }, { rejectWithValue }) => {
    try {
      console.log('❌ BorrowRequests: Denying request:', requestId);
      
      const action: BorrowRequestActionSchema = {
        action: 'DENY',
        response_message: responseMessage,
      };
      
      const response = await api.manageBorrowRequest(requestId, action);
      
      console.log('✅ BorrowRequests: Request denied successfully:', requestId);
      
      return response;
    } catch (error: any) {
      console.error('❌ BorrowRequests: Failed to deny request:', error);
      // FastAPI surfaces the human reason in `detail` ("Borrow request
      // not found or cannot be modified" on stale-state 404s). Prefer
      // it; fall back to `.message` for any non-FastAPI endpoints.
      return rejectWithValue(
        error.response?.data?.detail ||
          error.response?.data?.message ||
          'Failed to deny request',
      );
    }
  }
);

// Refresh all requests
export const refreshAllRequests = createAsyncThunk<
  BorrowRequestListResponse,
  void,
  { state: RootState }
>(
  'borrowRequests/refreshAllRequests',
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      console.log('🔄 BorrowRequests: Refreshing all requests');
      
      const state = getState();
      const currentFilter = state.borrowRequests.currentFilter;
      
      // Load with current filter
      const params: BorrowRequestsQuery = currentFilter === 'all' ? {} : { status: currentFilter.toUpperCase() as any };
      
      const response = await api.getBorrowRequests(params);
      
      console.log('✅ BorrowRequests: All requests refreshed');
      
      return response;
    } catch (error: any) {
      console.error('❌ BorrowRequests: Failed to refresh requests:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to refresh');
    }
  }
);

// ===============================
// SLICE DEFINITION
// ===============================

const borrowRequestsSlice = createSlice({
  name: 'borrowRequests',
  initialState,
  reducers: {
    // Filter Management
    setCurrentFilter: (state, action: PayloadAction<'all' | 'pending' | 'approved' | 'denied' | 'expired'>) => {
      console.log('🔧 BorrowRequests: Setting filter:', action.payload);
      state.currentFilter = action.payload;
    },
    
    setActiveTab: (state, action: PayloadAction<'received' | 'sent'>) => {
      console.log('📑 BorrowRequests: Setting active tab:', action.payload);
      state.activeTab = action.payload;
    },
    
    // Request Selection
    setSelectedRequest: (state, action: PayloadAction<BorrowRequestDetails | null>) => {
      console.log('📍 BorrowRequests: Setting selected request:', action.payload?.id || 'none');
      state.selectedRequest = action.payload;
    },
    
    // Add sent request (when user creates a request)
    addSentRequest: (state, action: PayloadAction<BorrowRequestResponse>) => {
      console.log('📤 BorrowRequests: Adding sent request:', action.payload.request_id);
      state.sentRequests.unshift(action.payload);
      state.totalSent = state.sentRequests.length;
    },
    
    // Update request status after management action
    updateRequestStatus: (state, action: PayloadAction<{ requestId: number; status: string }>) => {
      console.log('🔄 BorrowRequests: Updating request status:', action.payload);
      const { requestId, status } = action.payload;
      
      const requestIndex = state.receivedRequests.findIndex(req => req.id === requestId);
      if (requestIndex !== -1) {
        state.receivedRequests[requestIndex].status = status;
        
        // Update pending count
        state.pendingCount = state.receivedRequests.filter(req => req.status === 'PENDING').length;
      }
    },
    
    // Error Management
    clearError: (state) => {
      console.log('🧹 BorrowRequests: Clearing error');
      state.error = null;
    },
    
    clearManagementError: (state) => {
      console.log('🧹 BorrowRequests: Clearing management error');
      state.managementError = null;
    },
    
    // Auto-refresh control
    setAutoRefreshEnabled: (state, action: PayloadAction<boolean>) => {
      console.log('🔄 BorrowRequests: Setting auto-refresh:', action.payload);
      state.autoRefreshEnabled = action.payload;
    },
    
    // Reset State
    resetBorrowRequestsState: (state) => {
      console.log('🔄 BorrowRequests: Resetting state');
      return initialState;
    },
  },
  extraReducers: (builder) => {
    // ===============================
    // Load Received Requests
    // ===============================
    builder
      .addCase(loadReceivedRequests.pending, (state) => {
        console.log('⏳ BorrowRequests: Loading received requests...');
        state.loading.received = true;
        state.error = null;
      })
      .addCase(loadReceivedRequests.fulfilled, (state, action) => {
        console.log('✅ BorrowRequests: Received requests loaded successfully');
        state.loading.received = false;
        state.receivedRequests = action.payload.requests;
        state.pendingCount = action.payload.total_pending;
        state.totalReceived = action.payload.total_all;
        state.lastUpdated = new Date().toISOString();
        state.error = null;
      })
      .addCase(loadReceivedRequests.rejected, (state, action) => {
        console.error('❌ BorrowRequests: Failed to load received requests');
        state.loading.received = false;
        state.error = action.payload as string;
      });

    // ===============================
    // Approve Borrow Request — FE-255 PR1: optimistic
    // ===============================
    //
    // .pending writes the new state immediately (status → APPROVED,
    // pendingCount −1) and stashes a snapshot in `inflight`.
    // .rejected restores from that snapshot. .fulfilled is now mostly
    // a no-op for state shape — it just clears the snapshot and the
    // loading flag.
    builder
      .addCase(approveBorrowRequest.pending, (state, action) => {
        const reqId = (action.meta?.arg as { requestId?: number })?.requestId;
        state.loading.managing = true;
        state.managementError = null;
        if (typeof reqId !== 'number') return;

        const idx = state.receivedRequests.findIndex((r) => r.id === reqId);
        // Row may have been refetched away while the user tapped. No
        // optimistic update possible — fall through; .fulfilled will
        // still reconcile selectedRequest if it matches.
        if (idx === -1) return;

        const prev = state.receivedRequests[idx];
        state.inflight[action.meta.requestId] = {
          type: 'approve',
          rowId: reqId,
          prevStatus: prev.status,
          prevPendingCount: state.pendingCount,
        };

        // ✨ OPTIMISTIC: status + counter flip NOW.
        state.receivedRequests[idx].status = 'APPROVED';
        if (state.selectedRequest && state.selectedRequest.id === reqId) {
          state.selectedRequest.status = 'APPROVED';
        }
        state.pendingCount = Math.max(0, state.pendingCount - 1);
      })
      .addCase(approveBorrowRequest.fulfilled, (state, action) => {
        state.loading.managing = false;
        state.managementError = null;
        // State already reflects APPROVED from .pending — just clean
        // up the snapshot so the inflight map doesn't grow unbounded.
        delete state.inflight[action.meta.requestId];
      })
      .addCase(approveBorrowRequest.rejected, (state, action) => {
        state.loading.managing = false;
        state.managementError = action.payload as string;
        const op = state.inflight[action.meta.requestId];
        if (!op || op.type !== 'approve') {
          // Snapshot already consumed (rare: same requestId rejected
          // twice via thunk re-dispatch) or was never taken (row
          // missing on .pending). Nothing to roll back.
          return;
        }
        const idx = state.receivedRequests.findIndex((r) => r.id === op.rowId);
        if (idx !== -1) {
          state.receivedRequests[idx].status = op.prevStatus;
          if (state.selectedRequest && state.selectedRequest.id === op.rowId) {
            state.selectedRequest.status = op.prevStatus;
          }
        }
        state.pendingCount = op.prevPendingCount;
        delete state.inflight[action.meta.requestId];
      });

    // ===============================
    // Deny Borrow Request — FE-255 PR1: optimistic (mirrors Approve)
    // ===============================
    builder
      .addCase(denyBorrowRequest.pending, (state, action) => {
        const reqId = (action.meta?.arg as { requestId?: number })?.requestId;
        state.loading.managing = true;
        state.managementError = null;
        if (typeof reqId !== 'number') return;

        const idx = state.receivedRequests.findIndex((r) => r.id === reqId);
        if (idx === -1) return;

        const prev = state.receivedRequests[idx];
        state.inflight[action.meta.requestId] = {
          type: 'deny',
          rowId: reqId,
          prevStatus: prev.status,
          prevPendingCount: state.pendingCount,
        };

        // ✨ OPTIMISTIC: status + counter flip NOW.
        state.receivedRequests[idx].status = 'DENIED';
        // FE-252 — write response_message into the local row so the
        // "Owner's response" / "Your response" block appears
        // immediately (the BE returns it on the next refetch via
        // BE-260, but the actor's own UI shouldn't have to wait).
        const responseMessage = (action.meta?.arg as { responseMessage?: string })
          ?.responseMessage;
        if (responseMessage) {
          (state.receivedRequests[idx] as any).response_message = responseMessage;
        }
        if (state.selectedRequest && state.selectedRequest.id === reqId) {
          state.selectedRequest.status = 'DENIED';
        }
        state.pendingCount = Math.max(0, state.pendingCount - 1);
      })
      .addCase(denyBorrowRequest.fulfilled, (state, action) => {
        state.loading.managing = false;
        state.managementError = null;
        delete state.inflight[action.meta.requestId];
      })
      .addCase(denyBorrowRequest.rejected, (state, action) => {
        state.loading.managing = false;
        state.managementError = action.payload as string;
        const op = state.inflight[action.meta.requestId];
        if (!op || op.type !== 'deny') return;
        const idx = state.receivedRequests.findIndex((r) => r.id === op.rowId);
        if (idx !== -1) {
          state.receivedRequests[idx].status = op.prevStatus;
          // Roll back the local response_message write — the BE
          // never persisted it, so the FE shouldn't claim it did.
          delete (state.receivedRequests[idx] as any).response_message;
          if (state.selectedRequest && state.selectedRequest.id === op.rowId) {
            state.selectedRequest.status = op.prevStatus;
          }
        }
        state.pendingCount = op.prevPendingCount;
        delete state.inflight[action.meta.requestId];
      });

    // ===============================
    // Refresh All Requests
    // ===============================
    builder
      .addCase(refreshAllRequests.pending, (state) => {
        console.log('⏳ BorrowRequests: Refreshing...');
        state.loading.refreshing = true;
      })
      .addCase(refreshAllRequests.fulfilled, (state, action) => {
        console.log('✅ BorrowRequests: Refreshed successfully');
        state.loading.refreshing = false;
        state.receivedRequests = action.payload.requests;
        state.pendingCount = action.payload.total_pending;
        state.totalReceived = action.payload.total_all;
        state.lastUpdated = new Date().toISOString();
        state.error = null;
      })
      .addCase(refreshAllRequests.rejected, (state, action) => {
        console.error('❌ BorrowRequests: Failed to refresh');
        state.loading.refreshing = false;
        state.error = action.payload as string;
      });

    // ===============================
    // Load Sent Requests
    // ===============================
    builder
      .addCase(loadSentRequests.pending, (state) => {
        state.loading.sent = true;
      })
      .addCase(loadSentRequests.fulfilled, (state, action) => {
        state.loading.sent = false;
        state.sentRequests = action.payload.requests as unknown as any;
        state.totalSent = action.payload.total_all;
      })
      .addCase(loadSentRequests.rejected, (state, action) => {
        state.loading.sent = false;
        state.error = action.payload as string;
      });

    // ===============================
    // Claim Sent Request
    // ===============================
    builder
      .addCase(claimSentRequest.pending, (state) => {
        state.loading.claiming = true;
        state.error = null;
      })
      .addCase(claimSentRequest.fulfilled, (state, action) => {
        state.loading.claiming = false;
        // Remove claimed request from sent list to reflect transition into shares
        const id = action.payload.requestId;
        state.sentRequests = state.sentRequests.filter((r: any) => (r.id || r.request_id) !== id);
        state.totalSent = state.sentRequests.length;
      })
      .addCase(claimSentRequest.rejected, (state, action) => {
        state.loading.claiming = false;
        state.error = action.payload as string;
      });

    // ===============================
    // Cancel Sent Request — FE-255 PR1: optimistic
    // ===============================
    //
    // .pending splices the row out immediately and stashes (row,
    // index) so .rejected can restore it at the same position. The
    // sent list is sometimes filtered/sorted in the UI; restoring at
    // the original index preserves visual continuity when rollback
    // fires.
    builder
      .addCase(cancelSentRequest.pending, (state, action) => {
        const reqId = (action.meta?.arg as { requestId?: number })?.requestId;
        state.managementError = null;
        if (typeof reqId !== 'number') return;
        const idx = state.sentRequests.findIndex(
          (r: any) => (r.id || r.request_id) === reqId,
        );
        if (idx === -1) return;
        state.inflight[action.meta.requestId] = {
          type: 'cancel',
          rowId: reqId,
          prevIndex: idx,
          prevRow: state.sentRequests[idx],
        };
        // ✨ OPTIMISTIC: remove now.
        state.sentRequests.splice(idx, 1);
        state.totalSent = state.sentRequests.length;
      })
      .addCase(cancelSentRequest.fulfilled, (state, action) => {
        // Row already removed in .pending; just clear the snapshot.
        delete state.inflight[action.meta.requestId];
      })
      .addCase(cancelSentRequest.rejected, (state, action) => {
        // Surface the failure via the standard toast pipeline (the
        // managementError useEffect in BorrowRequestsManager fires
        // the red toast). Pre-FE-255 there was no error surface here
        // because cancel never had a rejected handler — the row just
        // silently stayed. With optimistic UI we MUST tell the user
        // why their row came back.
        state.managementError =
          (action.payload as string) || 'Could not cancel the request.';
        const op = state.inflight[action.meta.requestId];
        if (!op || op.type !== 'cancel') return;
        // Re-insert at original index. If the list has been re-sorted
        // or refetched while the network was in flight, the index may
        // overshoot — clamp to the current length so we still restore
        // (just at the end, which is visually consistent with "the
        // BE refused, here's your row back").
        const safeIdx = Math.min(op.prevIndex, state.sentRequests.length);
        state.sentRequests.splice(safeIdx, 0, op.prevRow);
        state.totalSent = state.sentRequests.length;
        delete state.inflight[action.meta.requestId];
      });
  },
});

// ===============================
// ACTIONS EXPORT
// ===============================

export const {
  setCurrentFilter,
  setActiveTab,
  setSelectedRequest,
  addSentRequest,
  updateRequestStatus,
  clearError,
  clearManagementError,
  setAutoRefreshEnabled,
  resetBorrowRequestsState,
} = borrowRequestsSlice.actions;

// ===============================
// SELECTORS
// ===============================

// FE-220 hotfix — every selector defensively reads through `state.borrowRequests ?? {}`
// so consumers don't crash when a test store omits this slice. Returns the
// initialState defaults so the consumer always sees a sane shape.
const _safe = (state: RootState) =>
  ((state as any).borrowRequests ?? {}) as Partial<BorrowRequestsState>;
export const selectReceivedRequests = (state: RootState) => _safe(state).receivedRequests ?? [];
export const selectSentRequests = (state: RootState) => _safe(state).sentRequests ?? [];
export const selectSelectedRequest = (state: RootState) => _safe(state).selectedRequest ?? null;
export const selectCurrentFilter = (state: RootState) =>
  _safe(state).currentFilter ?? initialState.currentFilter;
export const selectActiveTab = (state: RootState) =>
  _safe(state).activeTab ?? initialState.activeTab;
export const selectBorrowRequestsLoading = (state: RootState) =>
  _safe(state).loading ?? initialState.loading;
export const selectBorrowRequestsError = (state: RootState) => _safe(state).error ?? null;
export const selectManagementError = (state: RootState) => _safe(state).managementError ?? null;
export const selectPendingCount = (state: RootState) => _safe(state).pendingCount ?? 0;
export const selectTotalReceived = (state: RootState) => _safe(state).totalReceived ?? 0;
export const selectTotalSent = (state: RootState) => _safe(state).totalSent ?? 0;

// FE-248 v2 — memoized per-item pending-request map for a group. The
// inline strip under each inventory row asks "do I have requests for
// this item_id?". Recomputing N×M filters per render would re-run on
// every parent state change; memoizing on (receivedRequests, groupId)
// keeps the reducer-style work O(N) once and O(1) per row lookup.
//
// The selector returns a Map keyed by item_id; values are arrays
// sorted by queue_position (ascending, null last) then created_at
// (ascending) — so the "★ NEXT IN LINE" row is always element [0].
//
// Pure helper that callers compose via createSelector for memoization.
export type PendingRequestsByItem = Map<number, BorrowRequestDetails[]>;

export const buildPendingRequestsByItem = (
  received: readonly BorrowRequestDetails[],
  groupId: number,
): PendingRequestsByItem => {
  const byItem: PendingRequestsByItem = new Map();
  for (const r of received ?? []) {
    if ((r as any).group_id !== groupId) continue;
    const status = String((r as any).status ?? '').toUpperCase();
    if (status !== 'PENDING' && status !== 'WAITLISTED') continue;
    const itemId = (r as any).item_id as number | undefined;
    if (typeof itemId !== 'number') continue;
    const list = byItem.get(itemId);
    if (list) list.push(r);
    else byItem.set(itemId, [r]);
  }
  for (const [, list] of byItem) {
    list.sort((a: any, b: any) => {
      const qa = a.queue_position ?? Number.MAX_SAFE_INTEGER;
      const qb = b.queue_position ?? Number.MAX_SAFE_INTEGER;
      if (qa !== qb) return qa - qb;
      const ta = new Date(a.created_at ?? 0).getTime();
      const tb = new Date(b.created_at ?? 0).getTime();
      return ta - tb;
    });
  }
  return byItem;
};
export const selectLastUpdated = (state: RootState) => state.borrowRequests.lastUpdated;
export const selectAutoRefreshEnabled = (state: RootState) => state.borrowRequests.autoRefreshEnabled;

// Computed selectors
export const selectIsLoadingReceived = (state: RootState) => state.borrowRequests.loading.received;
export const selectIsLoadingSent = (state: RootState) => state.borrowRequests.loading.sent;
export const selectIsManaging = (state: RootState) => state.borrowRequests.loading.managing;
export const selectIsRefreshing = (state: RootState) => state.borrowRequests.loading.refreshing;

// Filtered requests based on current filter
export const selectFilteredReceivedRequests = (state: RootState) => {
  const requests = state.borrowRequests.receivedRequests;
  const filter = state.borrowRequests.currentFilter;
  
  if (filter === 'all') {
    return requests;
  }
  
  return requests.filter(request => 
    request.status.toLowerCase() === filter.toLowerCase()
  );
};

// Pending requests only
export const selectPendingRequests = (state: RootState) => 
  state.borrowRequests.receivedRequests.filter(request => request.status === 'PENDING');

// Recent requests (last 7 days)
export const selectRecentRequests = (state: RootState) => {
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  
  return state.borrowRequests.receivedRequests.filter(request => 
    new Date(request.created_at) >= sevenDaysAgo
  );
};

// Expired requests
export const selectExpiredRequests = (state: RootState) => {
  const now = new Date();
  return state.borrowRequests.receivedRequests.filter(request => 
    new Date(request.expires_at) <= now && request.status === 'PENDING'
  );
};

// Requests expiring soon (within 24 hours)
export const selectRequestsExpiringSoon = (state: RootState) => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  return state.borrowRequests.receivedRequests.filter(request => 
    new Date(request.expires_at) <= tomorrow && request.status === 'PENDING'
  );
};

export default borrowRequestsSlice.reducer;